import { useCallback, useEffect, useState } from 'react'

import {
  fetchDurationTestCount,
  fetchDurationTestFinishCount,
} from '@/pages/durationTest/loaders'
import { fetchDeviceVerifyCount } from '@/pages/inService/loaders'

export const useDeviceVerifyCount = () => {
  const [verifyDeviceStatusCount, setVerifyDeviceStatusCount] =
    useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const fetchCount = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const count = await fetchDeviceVerifyCount()
      setVerifyDeviceStatusCount(count)
    } catch (err) {
      console.error('Failed to fetch device verify count:', err)
      setError(
        'Failed to fetch device verify count: ' +
          (err instanceof Error ? err.message : String(err)),
      )
      setVerifyDeviceStatusCount(0)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCount()
  }, [fetchCount])

  // Auto-refresh count every 30 seconds to keep badge updated
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCount()
    }, 30000)

    return () => clearInterval(interval)
  }, [fetchCount])

  return {
    verifyDeviceStatusCount,
    loading,
    error,
    refetch: fetchCount,
  }
}

export const useDurationTestCount = () => {
  const [durationTestCount, setDurationTestCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const fetchCount = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const count = await fetchDurationTestCount()
      setDurationTestCount(count)
    } catch (err) {
      console.error('Failed to fetch duration test count:', err)
      setError(
        'Failed to fetch duration test count: ' +
          (err instanceof Error ? err.message : String(err)),
      )
      setDurationTestCount(0)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCount()
  }, [fetchCount])

  // Auto-refresh count every 30 seconds to keep badge updated
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCount()
    }, 30000)

    return () => clearInterval(interval)
  }, [fetchCount])

  return {
    durationTestCount,
    loading,
    error,
    refetch: fetchCount,
  }
}

export const useFinishDurationTestCount = () => {
  const [finishCount, setFinishCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const fetchCount = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const count = await fetchDurationTestFinishCount()
      setFinishCount(count)
    } catch (err) {
      console.error('Failed to fetch finish duration test count:', err)
      setError(
        'Failed to fetch finish duration test count: ' +
          (err instanceof Error ? err.message : String(err)),
      )
      setFinishCount(0)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCount()
  }, [fetchCount])

  useEffect(() => {
    const interval = setInterval(() => {
      fetchCount()
    }, 30000)
    return () => clearInterval(interval)
  }, [fetchCount])

  return { finishCount, loading, error, refetch: fetchCount }
}
